import { create } from 'zustand';
import type { ConversationWithUnread } from '@/types';

interface ConversationStore {
  conversations: ConversationWithUnread[];
  setConversations: (conversations: ConversationWithUnread[]) => void;
  mergeFromServer: (serverConversations: ConversationWithUnread[]) => void;
  /**
   * Moves a conversation to the top of the list (or adds it if missing).
   * Updates the last message preview and timestamp.
   */
  moveToTop: (echoId: string, content: string, timestamp: string, incrementUnread: boolean) => void;
  markAsRead: (echoId: string) => void;
  /** Resets the store to its initial empty state. Used on logout to prevent cross-account leaks. */
  reset: () => void;
}

/**
 * Conversation-list store — keeps contact-list ordering, unread counts, and
 * last-message previews in sync across components without round-tripping the
 * server. `mergeFromServer` reconciles a fresh list while preserving local-only
 * stubs; `moveToTop` is called by `useChat` on every send.
 */
export const useConversationStore = create<ConversationStore>((set) => ({
  conversations: [],

  setConversations: (conversations) => set({ conversations }),

  mergeFromServer: (serverConversations: ConversationWithUnread[]) =>
    set((state) => {
      // Build lookup of current local state
      const localMap = new Map<string, ConversationWithUnread>();
      state.conversations.forEach((conv) => localMap.set(conv.echo_id, conv));

      // Merge: for each server conversation, keep whichever has the newer last_message_at
      const merged = serverConversations.map((serverConv) => {
        const localConv = localMap.get(serverConv.echo_id);
        if (!localConv) return serverConv;

        const localTime = localConv.last_message_at
          ? new Date(localConv.last_message_at).getTime()
          : 0;
        const serverTime = serverConv.last_message_at
          ? new Date(serverConv.last_message_at).getTime()
          : 0;

        // If local is newer (from moveToTop), keep local data
        if (localTime > serverTime) return localConv;
        // If same time, prefer local unread count (may have been marked read locally)
        if (localTime === serverTime) {
          return {
            ...serverConv,
            unread_count: Math.min(localConv.unread_count, serverConv.unread_count),
          };
        }
        return serverConv;
      });

      // Add any local-only conversations (new stubs not yet on server)
      const serverIds = new Set(serverConversations.map((conv) => conv.echo_id));
      const localOnly = state.conversations.filter((conv) => !serverIds.has(conv.echo_id));

      // Sort by last_message_at descending
      const all = [...localOnly, ...merged].sort((left, right) => {
        const leftTime = left.last_message_at ? new Date(left.last_message_at).getTime() : 0;
        const rightTime = right.last_message_at ? new Date(right.last_message_at).getTime() : 0;
        return rightTime - leftTime;
      });

      return { conversations: all };
    }),

  moveToTop: (echoId, content, timestamp, incrementUnread) =>
    set((state) => {
      const existing = state.conversations.find((conv) => conv.echo_id === echoId);

      if (existing) {
        const updated = {
          ...existing,
          last_message_content: content,
          last_message_at: timestamp,
          unread_count: incrementUnread ? (existing.unread_count || 0) + 1 : existing.unread_count,
        };

        const rest = state.conversations.filter((conv) => conv.echo_id !== echoId);
        return { conversations: [updated, ...rest] };
      }

      // If it's a new conversation locally, we might not have full details (like echo name/image)
      // Creating a stub allows sorting to work immediately. React Query fetch will enrich later.
      const newStub: ConversationWithUnread = {
        conversation_id: `stub-pending-${Date.now()}`,
        echo_id: echoId,
        echo_name: '',
        unread_count: incrementUnread ? 1 : 0,
        last_message_content: content,
        last_message_at: timestamp,
      };

      return { conversations: [newStub, ...state.conversations] };
    }),

  markAsRead: (echoId) =>
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.echo_id === echoId ? { ...conv, unread_count: 0 } : conv
      ),
    })),

  reset: () => set({ conversations: [] }),
}));
